import { existsSync, readFileSync } from "node:fs";
import { dirname, isAbsolute, join, resolve } from "node:path";
import { parse as parseJsonc } from "jsonc-parser";

type TsConfig = {
  compilerOptions?: {
    baseUrl?: string;
    paths?: Record<string, string[]>;
  };
  extends?: string;
};

/**
 * Find tsconfig.json or jsconfig.json
 */
function findTsConfigFile(startDir: string = process.cwd()): string | null {
  let currentDir = startDir;

  while (true) {
    const tsConfigPath = join(currentDir, "tsconfig.json");
    if (existsSync(tsConfigPath)) {
      return tsConfigPath;
    }

    const jsConfigPath = join(currentDir, "jsconfig.json");
    if (existsSync(jsConfigPath)) {
      return jsConfigPath;
    }

    const parentDir = dirname(currentDir);
    if (parentDir === currentDir) {
      return null;
    }
    currentDir = parentDir;
  }
}

/**
 * Parse tsconfig.json with extends support
 */
function parseTsConfig(configPath: string): TsConfig {
  const content = readFileSync(configPath, "utf-8");
  const config: TsConfig = parseJsonc(content) as TsConfig;

  if (config.extends) {
    const extendsPath = resolve(dirname(configPath), config.extends);
    if (existsSync(extendsPath)) {
      const baseConfig = parseTsConfig(extendsPath);
      return {
        compilerOptions: {
          ...baseConfig.compilerOptions,
          ...config.compilerOptions,
          paths: {
            ...baseConfig.compilerOptions?.paths,
            ...config.compilerOptions?.paths,
          },
        },
      };
    }
  }

  return config;
}

/**
 * Resolve path alias to actual path
 * Examples:
 *   @/components/icons -> ./src/components/icons
 *   ~/lib/utils -> ./lib/utils
 */
export function resolvePathAlias(
  inputPath: string,
  projectRoot: string = process.cwd(),
): string {
  // If already absolute or relative path, return as-is
  if (
    isAbsolute(inputPath) ||
    inputPath.startsWith("./") ||
    inputPath.startsWith("../")
  ) {
    return inputPath;
  }

  // Find config file
  const configPath = findTsConfigFile(projectRoot);
  if (!configPath) {
    // No config found, treat as relative path
    return inputPath;
  }

  const config = parseTsConfig(configPath);
  const { baseUrl, paths } = config.compilerOptions || {};

  if (!paths) {
    // No path aliases configured
    return inputPath;
  }

  // Try to match path aliases
  for (const [alias, targets] of Object.entries(paths)) {
    // Convert alias pattern to regex
    // @/* -> ^@/(.*)$
    // ~/* -> ^~/(.*)$
    const pattern = alias.replace("*", "(.*)");
    const regex = new RegExp(`^${pattern}$`);
    const match = RegExp(regex).exec(inputPath);

    if (match) {
      // Get the first target (usually the only one)
      const target = targets[0];
      if (!target) continue;

      // Replace * in target with matched group
      const resolvedPath = target.replace("*", match[1] || "");

      // Resolve relative to baseUrl or config directory
      const configDir = dirname(configPath);
      const base = baseUrl ? resolve(configDir, baseUrl) : configDir;

      return resolve(base, resolvedPath);
    }
  }

  // No alias matched, return original
  return inputPath;
}
